import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import { PublicShell, TerminalMessage } from './PublicShell';
import { IntroView } from './IntroView';
import { SurveyRunner } from './SurveyRunner';
import { SubmittedView } from './SubmittedView';
import { fetchPublicSurvey, type PublicSurveyFetch } from './publicApi';

// Phase 6B Phase D — the public survey page at /s/:token (outside SignedInGuard
// + AppShell, see src/App.tsx). Fetches the recipient/survey state and dispatches
// to the right surface. Mobile-first.
export function PublicSurveyPage() {
  const { token } = useParams<{ token: string }>();
  const [result, setResult] = useState<PublicSurveyFetch | null>(null);

  useEffect(() => {
    let live = true;
    setResult(null);
    if (token) fetchPublicSurvey(token).then((r) => live && setResult(r));
    return () => {
      live = false;
    };
  }, [token]);

  if (!result) {
    return (
      <PublicShell>
        <CenteredSpinner />
      </PublicShell>
    );
  }

  if (result.kind === 'error') {
    return <PublicShell>{terminalView(result)}</PublicShell>;
  }

  return (
    <PublicShell>
      <SurveyExperience dto={result.dto} token={token!} />
    </PublicShell>
  );
}

function terminalView(err: Extract<PublicSurveyFetch, { kind: 'error' }>) {
  switch (err.state) {
    case 'cancelled':
      return <TerminalMessage title="This survey has been cancelled." />;
    case 'closed':
      return (
        <TerminalMessage
          title="This survey is closed."
          body={
            err.closeDate
              ? `It closed on ${new Date(err.closeDate).toLocaleDateString('en-AU', { dateStyle: 'long' })}. If you have questions, reach out to your account manager.`
              : 'If you have questions, reach out to your account manager.'
          }
        />
      );
    case 'expired':
      return <TerminalMessage title="This survey link has expired." />;
    case 'rate_limited':
      return <TerminalMessage title="Too many requests" body={err.reason} />;
    case 'network':
      return <TerminalMessage title="Couldn't load the survey" body={err.reason} />;
    case 'not_found':
    default:
      return <TerminalMessage title="This survey link isn't valid." body="Please check the link from your invite." />;
  }
}

// Dispatches the 200 states: submitted → read-only; pending → intro → runner;
// in_progress (per_recipient resume) → straight into the runner.
function SurveyExperience({ dto, token }: { dto: import('../../../types/surveyPublic').PublicSurveyDto; token: string }) {
  const [started, setStarted] = useState(dto.responseStatus === 'in_progress');

  if (dto.state === 'submitted') {
    return <SubmittedView dto={dto} />;
  }
  if (!started) {
    return <IntroView intro={dto.composition.introScreen} onStart={() => setStarted(true)} />;
  }
  return <SurveyRunner dto={dto} token={token} />;
}
